import React, { useEffect } from 'react';
import axios, { AxiosError, AxiosResponse } from 'axios';

import { useRequiredWebSocket } from '../../hooks/useRequiredWebSocket';
import { type LooseObject } from '../../types/looseObject';
import { apiClient } from '../../utils/apiClient';
import { FRONTEND_DIAGNOSTIC_EVENT } from '../../utils/reportFrontendDiagnostic';

const FRONTEND_ERROR_REPO_NAME = 'app-ept-ai';
const FRONTEND_ERROR_APP = 'app.ept.ai';

/**
 * ErrorReporter
 *
 * Captures frontend errors and key HTTP failures and sends them to the backend
 * over the existing WebSocket connection using action "frontend_error".
 */
const ErrorReporter: React.FC = () => {
  const { sendMessage, webSocketStatus } = useRequiredWebSocket();

  const sendOverWebSocket = (payload: LooseObject) => {
    try {
      if (webSocketStatus?.current?.current === 'open') {
        const message = JSON.stringify({
          action: 'frontend_error',
          ...payload,
          repo_name: FRONTEND_ERROR_REPO_NAME,
          frontend_app: FRONTEND_ERROR_APP,
        });
        sendMessage(message);
      }
    } catch (_) {
      // Best-effort only; do not throw
    }
  };

  useEffect(() => {
    // 1) Global JS error handler
    const handleWindowError = (event: ErrorEvent) => {
      const payload = {
        type: 'js_error',
        message: event?.message,
        filename: event?.filename,
        lineno: event?.lineno,
        colno: event?.colno,
        stack: (event?.error && (event.error as Error)?.stack) || undefined,
        href: typeof window !== 'undefined' ? window.location.href : undefined,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        timestamp: new Date().toISOString(),
      };
      sendOverWebSocket(payload);
    };

    // 2) Unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event?.reason;

      // Extract meaningful message from various error formats
      let message: string;
      if (reason === undefined || reason === null) {
        message = 'Promise rejected with no reason (possibly expired auth token)';
      } else if (typeof reason === 'string') {
        message = reason || 'Empty string rejection';
      } else if (reason instanceof Error) {
        message = reason.message || reason.name || 'Error with no message';
      } else if (typeof reason === 'object') {
        // Handle Auth0 errors and other object-based errors
        message =
          reason.message ||
          reason.error_description ||
          reason.error ||
          reason.toString?.() ||
          'Object rejection with no message';
      } else {
        message = String(reason);
      }

      const payload = {
        type: 'unhandled_rejection',
        message,
        stack: reason?.stack,
        error_type: reason?.constructor?.name,
        href: typeof window !== 'undefined' ? window.location.href : undefined,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        timestamp: new Date().toISOString(),
      };
      sendOverWebSocket(payload);
    };

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection as EventListener);

    const handleFrontendDiagnostic = (event: Event) => {
      const detail = (event as CustomEvent<LooseObject>).detail;
      if (!detail || typeof detail !== 'object') {
        return;
      }
      sendOverWebSocket({
        ...detail,
        href: typeof window !== 'undefined' ? window.location.href : undefined,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        timestamp: detail.timestamp ?? new Date().toISOString(),
      });
    };

    window.addEventListener(FRONTEND_DIAGNOSTIC_EVENT, handleFrontendDiagnostic);

    // 3) Wrap fetch to capture specific HTTP errors (400, 404, 503)
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (...args: Parameters<typeof fetch>) => {
      const res = await originalFetch(...args);
      try {
        if ([400, 404, 503].includes(res.status)) {
          const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url;
          const method =
            (typeof args[0] === 'object' && (args[1] as RequestInit)?.method) ||
            (typeof args[0] !== 'string' ? (args[0] as Request)?.method : undefined) ||
            'GET';
          const payload = {
            type: 'http_error',
            method,
            url,
            status: res.status,
            status_text: res.statusText,
            href: typeof window !== 'undefined' ? window.location.href : undefined,
            user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
            timestamp: new Date().toISOString(),
          };
          sendOverWebSocket(payload);
        }
      } catch (_) {
        // ignore
      }
      return res;
    };

    // 4) Axios interceptors for components using axios. Registered on both the
    // default axios singleton and the shared apiClient instance so requests
    // routed through apiClient (e.g. the React Query layer) are still reported.
    const onResponse = (response: AxiosResponse) => {
      try {
        if ([400, 404, 503].includes(response.status)) {
          const payload = {
            type: 'http_error',
            method: response.config?.method?.toUpperCase(),
            url: response.config?.url,
            status: response.status,
            status_text: response.statusText,
            href: typeof window !== 'undefined' ? window.location.href : undefined,
            user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
            timestamp: new Date().toISOString(),
          };
          sendOverWebSocket(payload);
        }
      } catch (_) {
        // ignored
      }
      return response;
    };
    const onResponseError = (error: AxiosError) => {
      try {
        const status = error.response?.status;
        if (status && [400, 404, 500, 503].includes(status)) {
          const payload = {
            type: 'http_error',
            method: error.config?.method?.toUpperCase(),
            url: error.config?.url,
            status,
            status_text: error.response?.statusText,
            href: typeof window !== 'undefined' ? window.location.href : undefined,
            user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
            timestamp: new Date().toISOString(),
          };
          sendOverWebSocket(payload);
        }
      } catch (_) {
        // ignored
      }
      return Promise.reject(error);
    };

    const reqInterceptor = axios.interceptors.request.use(config => config);
    const resInterceptor = axios.interceptors.response.use(onResponse, onResponseError);
    const apiClientResInterceptor = apiClient.interceptors.response.use(
      onResponse,
      onResponseError
    );

    return () => {
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection as EventListener);
      window.removeEventListener(FRONTEND_DIAGNOSTIC_EVENT, handleFrontendDiagnostic);
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(resInterceptor);
      apiClient.interceptors.response.eject(apiClientResInterceptor);
      try {
        // Restore original fetch
        window.fetch = originalFetch;
      } catch (_) {
        // ignored
      }
    };
  }, [sendMessage, webSocketStatus]);

  return null;
};

export default ErrorReporter;
